import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';

import { TableModule, TablePageEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CheckboxModule } from 'primeng/checkbox';
import { DnPrint2Service } from '../../core/dn-print2.service';
import { ApiService, Company, DeliveryNote } from '../../core/api.service';
import { saveBlob } from '../../core/file-download';

@Component({
    selector: 'app-deliverynote',
    standalone: true,
    imports: [CommonModule, FormsModule, TableModule, ButtonModule, SelectModule, InputTextModule, InputNumberModule, DatePickerModule, DialogModule, TagModule, ToastModule, ConfirmDialogModule, IconFieldModule, InputIconModule, CheckboxModule],
    providers: [MessageService, ConfirmationService],
    template: `
        <p-toast></p-toast>
        <p-confirmDialog></p-confirmDialog>

        <!-- Upload -->
        <div class="card mb-8">
            <div class="font-semibold text-xl mb-4"><i class="pi pi-upload mr-2"></i>Delivery Note Upload</div>
            <div class="flex flex-wrap gap-4 items-end">
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Company</label>
                    <p-select [options]="companies" [(ngModel)]="selectedCompanyId" optionLabel="name" optionValue="id" placeholder="Select company" [filter]="true" styleClass="w-72"></p-select>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Excel File (.xlsx)</label>
                    <input type="file" accept=".xlsx,.xls" (change)="onFileSelect($event)" class="p-2 border border-surface rounded-border" />
                </div>
                <div>
                    <p-button label="Upload" icon="pi pi-cloud-upload" [loading]="uploading" [disabled]="!selectedFile" (onClick)="onUpload()"></p-button>
                </div>
                <div>
                    <p-button label="Download Template" icon="pi pi-download" severity="secondary" (onClick)="downloadTemplate()"></p-button>
                </div>
            </div>
            <small *ngIf="selectedFile" class="text-green-600 mt-3 block"><i class="pi pi-check-circle mr-1"></i>{{ selectedFile.name }}</small>
            <small class="text-muted-color mt-2 block">Note: Columns are matched by their names, so the order of the columns does not matter.</small>
        </div>

        <!-- Table -->
        <div class="card">
            <div class="flex flex-wrap gap-4 items-end mb-4">
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Filter by Company</label>
                    <p-select [options]="companies" [(ngModel)]="filterCompanyId" optionLabel="name" optionValue="id" placeholder="All Companies" [showClear]="true" (onChange)="onFilterChange()" styleClass="w-64"></p-select>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">From Date</label>
                    <p-datepicker [(ngModel)]="fromDate" dateFormat="dd-mm-yy" [showIcon]="true" [showClear]="true" appendTo="body" placeholder="From" styleClass="w-44"></p-datepicker>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">To Date</label>
                    <p-datepicker [(ngModel)]="toDate" dateFormat="dd-mm-yy" [showIcon]="true" [showClear]="true" appendTo="body" placeholder="To" styleClass="w-44"></p-datepicker>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Search</label>
                    <p-iconfield iconPosition="left">
                        <p-inputicon styleClass="pi pi-search" />
                        <input type="text" pInputText [(ngModel)]="search" (keyup.enter)="onSearch()" placeholder="DN / Tank Lorry / Indent / To / From" class="w-72" />
                    </p-iconfield>
                </div>
                <div><p-button icon="pi pi-search" label="Search" (onClick)="onSearch()" severity="secondary"></p-button></div>
            </div>

            <div class="flex flex-wrap gap-2 items-center mb-4 p-3 rounded-border" style="background: var(--surface-100)">
                <span class="font-medium text-sm mr-2">{{ selectedIds.size }} selected</span>
                <p-button label="Select All Records" icon="pi pi-list-check" size="small" severity="secondary" (onClick)="selectAllRecords()"></p-button>
                <p-button label="Clear" icon="pi pi-times" size="small" severity="secondary" [outlined]="true" (onClick)="clearSelection()" [disabled]="selectedIds.size === 0"></p-button>
                <p-button label="Delete Selected" icon="pi pi-trash" size="small" severity="danger" (onClick)="deleteSelected()" [disabled]="selectedIds.size === 0"></p-button>
                <p-button label="Print DN" icon="pi pi-print" size="small" (onClick)="printDN()" [disabled]="selectedIds.size === 0"></p-button>
            </div>

            <p-table
                [value]="records"
                [lazy]="true"
                (onLazyLoad)="onPage($any($event))"
                [paginator]="true"
                [rows]="pageSize"
                [totalRecords]="total"
                [loading]="loading"
                [rowsPerPageOptions]="[10, 20, 50]"
                [scrollable]="true"
                scrollHeight="500px"
                [showCurrentPageReport]="true"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                styleClass="p-datatable-sm p-datatable-striped"
                [tableStyle]="{ 'min-width': '80rem' }"
            >
                <ng-template pTemplate="header">
                    <tr>
                        <th style="width:3rem" class="text-center">
                            <p-checkbox [binary]="true" [ngModel]="isPageAllSelected()" (onChange)="togglePage($event)"></p-checkbox>
                        </th>
                        <th style="width:4rem">Sr</th>
                        <th>DN Number</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Date</th>
                        <th>Tank Lorry</th>
                        <th>Indent</th>
                        <th>Quantity</th>
                        <th>Total Amb. Ltrs</th>
                        <th>Metric Tons</th>
                        <th style="width:8rem">Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr [class.bg-primary-50]="selectedIds.has(row.id)">
                        <td class="text-center"><p-checkbox [binary]="true" [ngModel]="selectedIds.has(row.id)" (onChange)="toggleRow(row.id)"></p-checkbox></td>
                        <td>{{ row.sr }}</td>
                        <td>{{ row.dnNumber }}</td>
                        <td>{{ row.fromLocation }}</td>
                        <td>{{ row.toLocation }}</td>
                        <td>{{ row.documentDate ? (row.documentDate | date: 'dd-MMM-yyyy') : '' }}</td>
                        <td>{{ row.tankLorryNo }}</td>
                        <td>{{ row.indentNumber }}</td>
                        <td>{{ row.quantity | number: '1.0-2' }}</td>
                        <td>{{ row.totalAmbientLitters | number: '1.0-2' }}</td>
                        <td>{{ row.metricTons | number: '1.0-4' }}</td>
                        <td>
                            <p-button icon="pi pi-pencil" severity="info" [text]="true" (onClick)="openEdit(row)"></p-button>
                            <p-button icon="pi pi-trash" severity="danger" [text]="true" (onClick)="confirmDelete(row)"></p-button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="12" class="text-center p-8 text-muted-color"><i class="pi pi-inbox text-2xl block mb-2"></i>No records. Upload an Excel file.</td>
                    </tr>
                </ng-template>
            </p-table>
        </div>

        <!-- Edit dialog -->
        <p-dialog header="Edit Delivery Note" [(visible)]="editDialog" [modal]="true" [style]="{ width: '900px' }" [draggable]="false" [maximizable]="true">
            <div class="grid grid-cols-2 gap-3">
                <div class="col-span-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Basic Info</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">From (Location)</label><input pInputText [(ngModel)]="editItem.fromLocation" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">To (Location)</label><input pInputText [(ngModel)]="editItem.toLocation" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Date</label><p-datepicker [(ngModel)]="editItem.documentDate" dateFormat="dd-mm-yy" [showIcon]="true" appendTo="body"></p-datepicker></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">DN Number</label><input pInputText [(ngModel)]="editItem.dnNumber" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank Lorry No.</label><input pInputText [(ngModel)]="editItem.tankLorryNo" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Indent Number</label><input pInputText [(ngModel)]="editItem.indentNumber" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Quantity</label><p-inputnumber [(ngModel)]="editItem.quantity" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Calibration No.</label><input pInputText [(ngModel)]="editItem.calibrationNo" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Calibration Expiry</label><input pInputText [(ngModel)]="editItem.calibrationExpiry" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Carriage Cont.</label><input pInputText [(ngModel)]="editItem.carriageContractor" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Delivery Address</label><input pInputText [(ngModel)]="editItem.deliveryAddress" /></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Quantity (Ch)</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Quantity Ch1</label><p-inputnumber [(ngModel)]="editItem.quantityCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Quantity Ch2</label><p-inputnumber [(ngModel)]="editItem.quantityCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Quantity Ch3</label><p-inputnumber [(ngModel)]="editItem.quantityCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Quantity Ch4</label><p-inputnumber [(ngModel)]="editItem.quantityCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Quantity Ch5</label><p-inputnumber [(ngModel)]="editItem.quantityCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Product (Ch)</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Product Ch1</label><input pInputText [(ngModel)]="editItem.productCh1" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Product Ch2</label><input pInputText [(ngModel)]="editItem.productCh2" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Product Ch3</label><input pInputText [(ngModel)]="editItem.productCh3" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Product Ch4</label><input pInputText [(ngModel)]="editItem.productCh4" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Product Ch5</label><input pInputText [(ngModel)]="editItem.productCh5" /></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Load Ref.Dip</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Ref.Dip Ch1</label><p-inputnumber [(ngModel)]="editItem.loadRefDipCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Ref.Dip Ch2</label><p-inputnumber [(ngModel)]="editItem.loadRefDipCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Ref.Dip Ch3</label><p-inputnumber [(ngModel)]="editItem.loadRefDipCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Ref.Dip Ch4</label><p-inputnumber [(ngModel)]="editItem.loadRefDipCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Ref.Dip Ch5</label><p-inputnumber [(ngModel)]="editItem.loadRefDipCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Load Prod.Dip</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Prod.Dip Ch1</label><p-inputnumber [(ngModel)]="editItem.loadProdDipCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Prod.Dip Ch2</label><p-inputnumber [(ngModel)]="editItem.loadProdDipCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Prod.Dip Ch3</label><p-inputnumber [(ngModel)]="editItem.loadProdDipCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Prod.Dip Ch4</label><p-inputnumber [(ngModel)]="editItem.loadProdDipCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Prod.Dip Ch5</label><p-inputnumber [(ngModel)]="editItem.loadProdDipCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Load Temp</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Temp(°F) Ch1</label><p-inputnumber [(ngModel)]="editItem.loadTempCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Temp(°F) Ch2</label><p-inputnumber [(ngModel)]="editItem.loadTempCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Temp(°F) Ch3</label><p-inputnumber [(ngModel)]="editItem.loadTempCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Temp(°F) Ch4</label><p-inputnumber [(ngModel)]="editItem.loadTempCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Temp(°F) Ch5</label><p-inputnumber [(ngModel)]="editItem.loadTempCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Load Density</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Density Ch1</label><p-inputnumber [(ngModel)]="editItem.loadDensityCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Density Ch2</label><p-inputnumber [(ngModel)]="editItem.loadDensityCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Density Ch3</label><p-inputnumber [(ngModel)]="editItem.loadDensityCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Density Ch4</label><p-inputnumber [(ngModel)]="editItem.loadDensityCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Load Density Ch5</label><p-inputnumber [(ngModel)]="editItem.loadDensityCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Recv Ref.Dip</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Ref.Dip Ch1</label><p-inputnumber [(ngModel)]="editItem.recvRefDipCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Ref.Dip Ch2</label><p-inputnumber [(ngModel)]="editItem.recvRefDipCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Ref.Dip Ch3</label><p-inputnumber [(ngModel)]="editItem.recvRefDipCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Ref.Dip Ch4</label><p-inputnumber [(ngModel)]="editItem.recvRefDipCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Ref.Dip Ch5</label><p-inputnumber [(ngModel)]="editItem.recvRefDipCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Recv Prod.Dip</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Prod.Dip Ch1</label><p-inputnumber [(ngModel)]="editItem.recvProdDipCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Prod.Dip Ch2</label><p-inputnumber [(ngModel)]="editItem.recvProdDipCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Prod.Dip Ch3</label><p-inputnumber [(ngModel)]="editItem.recvProdDipCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Prod.Dip Ch4</label><p-inputnumber [(ngModel)]="editItem.recvProdDipCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Prod.Dip Ch5</label><p-inputnumber [(ngModel)]="editItem.recvProdDipCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Recv Temp</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Temp Ch1</label><p-inputnumber [(ngModel)]="editItem.recvTempCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Temp Ch2</label><p-inputnumber [(ngModel)]="editItem.recvTempCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Temp Ch3</label><p-inputnumber [(ngModel)]="editItem.recvTempCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Temp Ch4</label><p-inputnumber [(ngModel)]="editItem.recvTempCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Temp Ch5</label><p-inputnumber [(ngModel)]="editItem.recvTempCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Recv Density</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Density Ch1</label><p-inputnumber [(ngModel)]="editItem.recvDensityCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Density Ch2</label><p-inputnumber [(ngModel)]="editItem.recvDensityCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Density Ch3</label><p-inputnumber [(ngModel)]="editItem.recvDensityCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Density Ch4</label><p-inputnumber [(ngModel)]="editItem.recvDensityCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Recv Density Ch5</label><p-inputnumber [(ngModel)]="editItem.recvDensityCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Short InMM</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Short InMM Ch1</label><p-inputnumber [(ngModel)]="editItem.shortInMMCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Short InMM Ch2</label><p-inputnumber [(ngModel)]="editItem.shortInMMCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Short InMM Ch3</label><p-inputnumber [(ngModel)]="editItem.shortInMMCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Short InMM Ch4</label><p-inputnumber [(ngModel)]="editItem.shortInMMCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Short InMM Ch5</label><p-inputnumber [(ngModel)]="editItem.shortInMMCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Short InLtrs</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Short InLtrs Ch1</label><p-inputnumber [(ngModel)]="editItem.shortInLtrsCh1" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Short InLtrs Ch2</label><p-inputnumber [(ngModel)]="editItem.shortInLtrsCh2" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Short InLtrs Ch3</label><p-inputnumber [(ngModel)]="editItem.shortInLtrsCh3" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Short InLtrs Ch4</label><p-inputnumber [(ngModel)]="editItem.shortInLtrsCh4" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Short InLtrs Ch5</label><p-inputnumber [(ngModel)]="editItem.shortInLtrsCh5" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="col-span-2 mt-2"><div class="font-semibold text-sm border-b pb-1 mb-2">Totals & Other</div></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Seal Number</label><input pInputText [(ngModel)]="editItem.sealNumber" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Seal Total</label><input pInputText [(ngModel)]="editItem.sealTotal" /></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Cumul.Short Amb.Ltrs</label><p-inputnumber [(ngModel)]="editItem.cumulShortAmbLtrs" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Cumul.Short Std.Ltrs</label><p-inputnumber [(ngModel)]="editItem.cumulShortStdLtrs" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Cumul.Short M.Tons</label><p-inputnumber [(ngModel)]="editItem.cumulShortMTons" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">NetRecv Amb.Ltrs</label><p-inputnumber [(ngModel)]="editItem.netRecvAmbLtrs" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">NetRecv Std.Ltrs</label><p-inputnumber [(ngModel)]="editItem.netRecvStdLtrs" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">NetRecv M.Tons</label><p-inputnumber [(ngModel)]="editItem.netRecvMTons" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Total Ambient Litters</label><p-inputnumber [(ngModel)]="editItem.totalAmbientLitters" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Metric Tons</label><p-inputnumber [(ngModel)]="editItem.metricTons" [useGrouping]="false" [maxFractionDigits]="4"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Authorized Signature</label><input pInputText [(ngModel)]="editItem.authorizedSignature" /></div>
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="editDialog = false"></p-button>
                <p-button label="Save" icon="pi pi-check" (onClick)="saveEdit()"></p-button>
            </ng-template>
        </p-dialog>
    `
})
export class DeliveryNotePage implements OnInit {
    companies: Company[] = [];
    selectedCompanyId: number | null = null;
    filterCompanyId: number | null = null;
    fromDate: Date | null = null;
    toDate: Date | null = null;

    selectedFile: File | null = null;
    uploading = false;

    records: DeliveryNote[] = [];
    total = 0;
    page = 1;
    pageSize = 10;
    loading = false;
    search = '';

    selectedIds = new Set<number>();

    editDialog = false;
    editItem: DeliveryNote = {} as DeliveryNote;

    constructor(
        private api: ApiService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private cd: ChangeDetectorRef,
        private dnPrint: DnPrint2Service
    ) {}

    ngOnInit(): void {
        this.loadCompanies();
        this.loadData();
    }

    loadCompanies(): void {
        this.api.getCompanies().subscribe({
            next: (data) => {
                this.companies = data;
                this.cd.detectChanges();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load companies.' })
        });
    }

    onFileSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.selectedFile = input.files && input.files.length ? input.files[0] : null;
        this.cd.detectChanges();
    }

    onUpload(): void {
        if (!this.selectedCompanyId) {
            this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Please select a company.' });
            return;
        }
        if (!this.selectedFile) {
            this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Please select a file.' });
            return;
        }
        this.uploading = true;
        this.api.uploadDeliveryNote(this.selectedCompanyId, this.selectedFile).subscribe({
            next: (res: any) => {
                this.uploading = false;
                this.selectedFile = null;
                this.messageService.add({ severity: 'success', summary: 'Done', detail: res?.message || 'Uploaded.' });
                this.filterCompanyId = this.selectedCompanyId;
                this.page = 1;
                this.clearSelection();
                this.loadData();
                this.cd.detectChanges();
            },
            error: (err) => {
                this.uploading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Upload failed.' });
                this.cd.detectChanges();
            }
        });
    }

    downloadTemplate(): void {
        this.api.downloadDeliveryNoteTemplate().subscribe({
            next: (blob) => saveBlob(blob, 'DN_Template.xlsx'),
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not download template.' })
        });
    }

    loadData(): void {
        this.loading = true;
        this.api.getDeliveryNoteList(this.filterCompanyId, this.page, this.pageSize, this.search, this.toIso(this.fromDate), this.toIso(this.toDate)).subscribe({
            next: (res) => {
                this.records = res.data;
                this.total = res.total;
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load data.' });
                this.cd.detectChanges();
            }
        });
    }

    onPage(event: TablePageEvent): void {
        this.page = Math.floor(event.first / event.rows) + 1;
        this.pageSize = event.rows;
        this.loadData();
    }
    onFilterChange(): void {
        this.page = 1;
        this.clearSelection();
        this.loadData();
    }
    onSearch(): void {
        this.page = 1;
        this.clearSelection();
        this.loadData();
    }

    toggleRow(id: number): void {
        this.selectedIds.has(id) ? this.selectedIds.delete(id) : this.selectedIds.add(id);
        this.cd.detectChanges();
    }
    isPageAllSelected(): boolean {
        return this.records.length > 0 && this.records.every((r) => this.selectedIds.has(r.id));
    }
    togglePage(event: any): void {
        const checked = event?.checked ?? !this.isPageAllSelected();
        if (checked) this.records.forEach((r) => this.selectedIds.add(r.id));
        else this.records.forEach((r) => this.selectedIds.delete(r.id));
        this.cd.detectChanges();
    }
    selectAllRecords(): void {
        this.api.getDeliveryNoteIds(this.filterCompanyId, this.search, this.toIso(this.fromDate), this.toIso(this.toDate)).subscribe({
            next: (ids) => {
                ids.forEach((id) => this.selectedIds.add(id));
                this.messageService.add({ severity: 'info', summary: 'Selected', detail: `${ids.length} record(s) selected.` });
                this.cd.detectChanges();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not select all.' })
        });
    }
    clearSelection(): void {
        this.selectedIds.clear();
        this.cd.detectChanges();
    }

    deleteSelected(): void {
        if (this.selectedIds.size === 0) return;
        const ids = Array.from(this.selectedIds);
        this.confirmationService.confirm({
            message: `Delete ${ids.length} selected record(s)?`,
            header: 'Confirm Bulk Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, delete all',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.api.bulkDeleteDeliveryNote(ids).subscribe({
                    next: (res: any) => {
                        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: res?.message || 'Deleted.' });
                        this.clearSelection();
                        this.loadData();
                        this.cd.detectChanges();
                    },
                    error: () => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Bulk delete failed.' });
                        this.cd.detectChanges();
                    }
                });
            }
        });
    }

    openEdit(item: DeliveryNote): void {
        this.editItem = { ...item };
        this.editDialog = true;
    }
    saveEdit(): void {
        this.api.updateDeliveryNote(this.editItem.id, this.editItem).subscribe({
            next: () => {
                this.editDialog = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Record updated.' });
                this.loadData();
                this.cd.detectChanges();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Update failed.' });
                this.cd.detectChanges();
            }
        });
    }
    confirmDelete(item: DeliveryNote): void {
        this.confirmationService.confirm({
            message: `Delete DN "${item.dnNumber || 'this record'}"?`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, delete',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.api.deleteDeliveryNote(item.id).subscribe({
                    next: () => {
                        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Record deleted.' });
                        this.selectedIds.delete(item.id);
                        this.loadData();
                        this.cd.detectChanges();
                    },
                    error: () => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Delete failed.' });
                        this.cd.detectChanges();
                    }
                });
            }
        });
    }

    printDN(): void {
        if (this.selectedIds.size === 0) return;
        this.api.getDeliveryNoteList(this.filterCompanyId, 1, 100000, this.search, this.toIso(this.fromDate), this.toIso(this.toDate)).subscribe({
            next: (res) => {
                const rows = res.data.filter((r) => this.selectedIds.has(r.id));
                if (rows.length === 0) return;
                this.dnPrint.print(rows, this.companies);
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load records for printing.' })
        });
    }

    private toIso(d: Date | null): string | null {
        if (!d) return null;
        // local date -> yyyy-mm-dd (timezone shift se bachne ke liye)
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }
}
